import React, { useState, useEffect, useMemo } from "react";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Badge } from "../../../shared/components/Badge.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { Card } from "../../../shared/components/Card.jsx";
import { useInvoices } from "../../invoices/hooks/useInvoices.js";
import { useActiveCompany } from "../../../shared/context/ActiveCompanyContext.jsx";
import { InvoiceDetailModal } from "../../invoices/components/InvoiceDetailModal.jsx";
import { InvoicePrintModal } from "../../invoices/components/InvoicePrintModal.jsx";
import { PaymentTallyModal } from "../../invoices/components/PaymentTallyModal.jsx";
import { formatAppDate } from "../../../shared/utils/dateUtils.js";
import { useIncrementalList } from "../../../shared/hooks/useIncrementalList.js";
import { ListSentinel } from "../../../shared/components/ListSentinel.jsx";

/** Invoices of one customer: metrics, settle banner, search, list, detail/print. Used as the ledger sheet's Invoices tab. */
export function CustomerInvoicesPanel({ customer }) {
  const { invoices, loading, recordPayment, removeInvoice, refetch: refetchInvoices } = useInvoices();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInvoiceForDetail, setSelectedInvoiceForDetail] = useState(null);
  const [selectedInvoiceForPrint, setSelectedInvoiceForPrint] = useState(null);
  const [isTallyModalOpen, setIsTallyModalOpen] = useState(false);

  const { ownsRecord } = useActiveCompany();

  // This customer's invoices of the working company
  const customerInvoices = useMemo(() => {
    if (!customer?.id || !invoices) return [];
    return invoices.filter((inv) => {
      // Match by customerId or fallback to customer name match
      const matchesCustomer =
        inv.customerId === customer.id ||
        (inv.customerName && customer.name && inv.customerName.toLowerCase() === customer.name.toLowerCase());
      if (!matchesCustomer || !ownsRecord(inv)) return false;

      const term = searchTerm.toLowerCase().trim();
      if (!term) return true;
      const no = (inv.invoiceNo || "").toLowerCase();
      const date = (inv.invoiceDate || "").toLowerCase();
      return no.includes(term) || date.includes(term);
    });
  }, [invoices, customer, searchTerm, ownsRecord]);

  // Render in chunks of 30, appending as the user scrolls
  const invoiceList = useIncrementalList(customerInvoices, { resetKey: `${customer?.id}|${searchTerm}` });

  // Aggregate metrics
  const totalBilled = useMemo(() => {
    return customerInvoices.reduce((sum, inv) => sum + Number(inv.grandTotal || 0), 0);
  }, [customerInvoices]);

  const totalOutstanding = useMemo(() => {
    return customerInvoices.reduce((sum, inv) => sum + Number(inv.balance || 0), 0);
  }, [customerInvoices]);

  if (!customer) return null;

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {/* KPI Metrics row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem" }}>
          <div
            style={{
              background: "var(--bg-card)",
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-md)",
              padding: "0.75rem",
              textAlign: "center"
            }}
          >
            <div style={{ fontSize: "0.7rem", color: "var(--text-dim)", textTransform: "uppercase" }}>Invoices</div>
            <div style={{ fontSize: "1.1rem", fontWeight: "800", color: "var(--text-main)" }}>
              {customerInvoices.length}
            </div>
          </div>
          <div
            style={{
              background: "var(--bg-card)",
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-md)",
              padding: "0.75rem",
              textAlign: "center"
            }}
          >
            <div style={{ fontSize: "0.7rem", color: "var(--text-dim)", textTransform: "uppercase" }}>Total Billed</div>
            <div style={{ fontSize: "1.1rem", fontWeight: "800", color: "var(--accent-blue)" }}>
              ₹{totalBilled.toLocaleString("en-IN")}
            </div>
          </div>
          <div
            style={{
              background: "var(--bg-card)",
              border: totalOutstanding > 0 ? "1px solid var(--warning)" : "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-md)",
              padding: "0.75rem",
              textAlign: "center"
            }}
          >
            <div style={{ fontSize: "0.7rem", color: "var(--text-dim)", textTransform: "uppercase" }}>Outstanding</div>
            <div
              style={{
                fontSize: "1.1rem",
                fontWeight: "800",
                color: totalOutstanding > 0 ? "var(--warning)" : "var(--success)"
              }}
            >
              ₹{totalOutstanding.toLocaleString("en-IN")}
            </div>
          </div>
        </div>

        {/* Outstanding Payment Tally Banner */}
        {totalOutstanding > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "rgba(245, 158, 11, 0.08)",
              border: "1px solid rgba(245, 158, 11, 0.3)",
              borderRadius: "var(--radius-md)",
              padding: "0.6rem 0.85rem",
              gap: "0.5rem",
              flexWrap: "wrap"
            }}
          >
            <div style={{ fontSize: "0.82rem", color: "var(--text-main)" }}>
              <span>Outstanding balance of </span>
              <strong style={{ color: "var(--warning)" }}>₹{totalOutstanding.toLocaleString("en-IN")}</strong>
              <span> across open invoices.</span>
            </div>
            <Button
              size="sm"
              variant="primary"
              onClick={() => setIsTallyModalOpen(true)}
              style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem", minHeight: "28px" }}
            >
              💰 Settle Invoices (FIFO)
            </Button>
          </div>
        )}

        {/* Search Input */}
        <div className="search-container" style={{ margin: 0 }}>
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input"
            placeholder="Search by invoice number or date..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Invoices List */}
        {loading ? (
          <div className="state-box">
            <div className="state-icon">⏳</div>
            <div className="state-title">Loading Invoices...</div>
          </div>
        ) : customerInvoices.length === 0 ? (
          <div className="state-box">
            <div className="state-icon">🧾</div>
            <div className="state-title">No invoices found for this party</div>
            <div className="state-desc">
              {searchTerm ? "No bills match your search criteria." : "No invoices have been billed to this customer yet."}
            </div>
          </div>
        ) : (
          <div
            style={{
              maxHeight: "55vh",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "0.6rem",
              paddingRight: "4px"
            }}
          >
            {invoiceList.visibleItems.map((inv) => {
              const isNonGst = inv.invoiceType === "Normal" || inv.isNonGst;
              const statusVariant =
                inv.paymentStatus === "Paid" ? "success" : inv.paymentStatus === "Partial" ? "warning" : "danger";

              return (
                <Card
                  key={inv.id}
                  style={{
                    padding: "0.85rem",
                    cursor: "pointer",
                    transition: "transform 0.15s ease, border-color 0.15s ease"
                  }}
                  onClick={() => setSelectedInvoiceForDetail(inv)}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
                        <span style={{ fontWeight: "700", fontSize: "0.95rem", color: "var(--text-main)" }}>
                          {inv.invoiceNo}
                        </span>
                        <Badge variant={isNonGst ? "neutral" : "primary"} style={{ fontSize: "0.65rem" }}>
                          {isNonGst ? "Normal Bill" : "GST Tax"}
                        </Badge>
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: "3px" }}>
                        📅 {formatAppDate(inv.invoiceDate, inv.companyDetails?.dateFormat) || "N/A"} • {inv.lineItems?.length || 0} items
                      </div>
                      {inv.companyName && (
                        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: "2px" }}>
                          Issued by: <strong>{inv.companyName}</strong>
                        </div>
                      )}
                    </div>

                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: "1.1rem", fontWeight: "800", color: "var(--accent-blue)" }}>
                        ₹{Number(inv.grandTotal || 0).toLocaleString("en-IN")}
                      </div>
                      <Badge variant={statusVariant} style={{ fontSize: "0.68rem" }}>
                        {inv.paymentStatus || "Unpaid"}
                      </Badge>
                      {Number(inv.balance || 0) > 0 && (
                        <div style={{ fontSize: "0.7rem", color: "var(--warning)", marginTop: "2px", fontWeight: "600" }}>
                          Bal: ₹{Number(inv.balance).toLocaleString("en-IN")}
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "0.4rem",
                      marginTop: "0.6rem",
                      paddingTop: "0.5rem",
                      borderTop: "1px solid var(--border-subtle)"
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setSelectedInvoiceForPrint(inv)}
                      style={{ fontSize: "0.72rem", padding: "0.25rem 0.6rem", minHeight: "28px" }}
                    >
                      🖨️ PDF Print
                    </Button>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => setSelectedInvoiceForDetail(inv)}
                      style={{ fontSize: "0.72rem", padding: "0.25rem 0.6rem", minHeight: "28px" }}
                    >
                      View Details
                    </Button>
                  </div>
                </Card>
              );
            })}
            <ListSentinel
              sentinelRef={invoiceList.sentinelRef}
              hasMore={invoiceList.hasMore}
              visibleCount={invoiceList.visibleCount}
              total={invoiceList.total}
              onShowMore={invoiceList.showMore}
            />
          </div>
        )}

      </div>

      {/* Invoice Detail / Payment modal */}
      <InvoiceDetailModal
        isOpen={!!selectedInvoiceForDetail}
        onClose={() => setSelectedInvoiceForDetail(null)}
        invoice={selectedInvoiceForDetail}
        onRecordPayment={recordPayment}
        onDeleteInvoice={removeInvoice}
        onPaymentsChanged={refetchInvoices}
      />

      {/* Invoice PDF Print Modal */}
      <InvoicePrintModal
        isOpen={!!selectedInvoiceForPrint}
        onClose={() => setSelectedInvoiceForPrint(null)}
        invoice={selectedInvoiceForPrint}
      />

      {/* Multi-Invoice Payment Tally Modal */}
      <PaymentTallyModal
        isOpen={isTallyModalOpen}
        onClose={() => setIsTallyModalOpen(false)}
        preselectedCustomer={customer}
      />
    </>
  );
}

export function CustomerInvoicesModal({ isOpen, onClose, customer }) {
  if (!customer) return null;
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={`Invoices: ${customer.name}`} size="lg">
      <CustomerInvoicesPanel customer={customer} />
      <div style={{ display: "flex", justifyContent: "flex-end", borderTop: "1px solid var(--border-subtle)", paddingTop: "0.75rem", marginTop: "1rem" }}>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
    </BottomSheet>
  );
}

export default CustomerInvoicesModal;
